import TranslationNS from '@/utils/Constants/TranslationNS'
import { EVENT_HUB_TABS } from '@/components/EventHub/tabs.config'

const EVENT_PAGE = TranslationNS.EVENT_PAGE
const REVIEWS = TranslationNS.REVIEW_COMPETITION
const tabPath = (key) => EVENT_HUB_TABS.find((tab) => tab.key === key)?.path ?? key

/**
 * « Page de l'événement » (the hub in organizer mode): what the public sees, edited in place. Plays
 * on the hub root and on every tab. The first three steps use the current tab (same hero / tabs /
 * settings card everywhere but Leaderboard); the tab steps carry `navigate` (William, 28 Oct 2026:
 * « le guide doit être complet »), so the tour walks every tab and comes back where it started. The
 * dashboard tour deliberately never presents this content: it lives here. The publish pill above the
 * banner gets its own step: publishing is the point of the page.
 */
export const EVENT_PAGE_TOUR = {
    id: 'event-page',
    version: 1,
    paths: ['', ...EVENT_HUB_TABS.map((tab) => tab.path).filter(Boolean)],
    steps: [
        {
            key: 'hero',
            target: '[data-tour="hub-hero"]',
            placement: 'bottom',
            title: 'Your public page',
            // one sentence, no list: the hero is 580 px tall, a taller bubble would not fit under it
            description:
                'This is the page athletes and the public will see. Hover the banner, the logo or the name to change them right here; category, country and status update by themselves, and the stars open the Reviews tab.',
        },
        {
            key: 'tabs',
            target: '[data-tour="hub-tabs"]',
            placement: 'bottom',
            title: 'One tab per topic',
            description:
                'Description, categories, workouts, planning, ranking, gallery, reviews: each tab is filled in its own place, with the pencil. The tour visits each one.',
        },
        {
            key: 'settings',
            target: '[data-tour="hub-settings"]',
            // the card is 400-650 px tall: under the head, its bubble fits below on a phone
            scroll: 'start',
            placement: 'left',
            placementNarrow: 'bottom',
            navigate: tabPath('description'),
            title: 'The key information',
            description:
                'Dates and time zone, venue and map, category, qualification: the pencil opens the form, ✓ saves. The buttons show what the public gets: register, waiting list, tickets, staff.',
        },
        {
            key: 'publish',
            target: '[data-tour="hub-publish"]',
            placement: 'bottomRight',
            placementNarrow: 'bottomRight',
            navigate: tabPath('description'),
            title: 'Publish the page',
            description:
                'While this switch is off, only you see the page: prepare it quietly, then publish it when it is ready.',
        },
        {
            key: 'partners',
            target: '[data-tour="hub-partners"]',
            placement: 'bottom',
            navigate: tabPath('description'),
            title: 'Your partners',
            description: 'Their logos sit above the content of every tab. Hover one to delete it.',
            entries: [{ label: 'Add partner logo', ns: EVENT_PAGE, text: 'uploads a logo (600 × 200 works best).' }],
        },
        {
            key: 'description',
            target: '[data-tour="hub-tab-description"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('description'),
            title: 'The description',
            description:
                'Present your event: the pencil opens a text editor with pictures, ✓ saves, ✗ cancels. Pictures open full screen for visitors.',
        },
        {
            key: 'divisions',
            target: '[data-tour="hub-tab-divisions"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('divisions'),
            title: 'The categories',
            description:
                'One card per category with price, spots and fees, as athletes see them. Open a card to write its description. Categories are created in the registration settings.',
        },
        {
            key: 'workouts',
            target: '[data-tour="hub-tab-workouts"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('workouts'),
            title: 'The workouts',
            description: 'Each workout has a card: its name, its description and when the public may read it.',
            entries: [
                {
                    label: 'Add new workout',
                    ns: EVENT_PAGE,
                    text: 'creates a card; the pencils rename it and write it.',
                },
                {
                    label: 'Program publication',
                    ns: EVENT_PAGE,
                    text: 'a date from which the workout shows by itself.',
                },
                { label: 'Published', ns: EVENT_PAGE, text: 'the switch shows or hides it right now.' },
            ],
        },
        {
            key: 'planning',
            target: '[data-tour="hub-tab-planning"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('planning'),
            title: 'The planning',
            description:
                'The heats as the public sees them, with filters by name, category, workout or date. Shown once the planning is published; until then, a notice.',
        },
        {
            key: 'leaderboard',
            target: '[data-tour="hub-tab-leaderboard"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('leaderboard'),
            title: 'The ranking',
            description:
                'The public ranking: category, view, search, and the scores side by side. Scores are entered in « Score management ».',
        },
        {
            key: 'gallery',
            target: '[data-tour="hub-tab-gallery"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('gallery'),
            title: 'The gallery',
            description: 'Albums of photos. Open an album to name it, add photos and publish it.',
            entries: [
                {
                    label: 'New album',
                    ns: EVENT_PAGE,
                    text: 'creates an album; it stays unpublished until you say so.',
                },
                { label: 'Add photos', ns: EVENT_PAGE, text: 'uploads pictures into the open album.' },
            ],
        },
        {
            key: 'reviews',
            target: '[data-tour="hub-tab-reviews"]',
            block: '[data-tour="hub-tab-head"]',
            placement: 'bottom',
            navigate: tabPath('reviews'),
            title: 'The reviews',
            description: 'What athletes wrote about your event, with their stars.',
            entries: [
                {
                    label: 'Public',
                    ns: REVIEWS,
                    text: 'the switch shows the reviews to everyone or keeps them for you.',
                },
                { label: 'Reply', ns: REVIEWS, text: 'answers a review under it, as the organizer.' },
            ],
        },
    ],
}

export default EVENT_PAGE_TOUR
